import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { itineraryApi } from '../services/api';
import { User, Lock, Mail, Loader2, Save, Edit3, Phone, Globe, X, Bell, ArrowLeft } from 'lucide-react';

import { NotificationToggle } from '../components/NotificationToggle';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    nationality: user?.nationality || '',
  });
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  const handleEditToggle = () => {
    if (editing) {
      // Cancel: reset to original values
      setProfile({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        phone: user?.phone || '',
        nationality: user?.nationality || '',
      });
    }
    setEditing(!editing);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await itineraryApi.updateProfile(profile);
      if (setUser) setUser(res.data);
      toast.success('Profile updated successfully!');
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwords.current || !passwords.newPass || !passwords.confirm) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (passwords.newPass !== passwords.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwords.newPass.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await itineraryApi.updateProfile({}); // dummy - use api directly
      const api = (await import('../services/api')).default;
      await api.post('/auth/change-password', {
        current_password: passwords.current,
        new_password: passwords.newPass,
      });
      toast.success('Password updated successfully!');
      setPasswords({ current: '', newPass: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="profile-page">
      {/* Premium Background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
        <div className="absolute top-20 left-10 w-96 h-96 bg-gold/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 right-20 w-80 h-80 bg-sky/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}} />
      </div>

      <Header />
      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-2xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-dashboard-top">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
            <div className="w-16 h-16 bg-white/15 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-4 border border-white/20">
              <User className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-white drop-shadow-lg mb-1">My Profile</h1>
            <p className="text-white/70">Manage your account settings</p>
          </motion.div>

          {/* Profile Info */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 p-7 mb-5">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-semibold text-ocean flex items-center gap-2">
                <User className="w-5 h-5 text-gold" /> Personal Information
              </h3>
              <Button
                variant={editing ? "outline" : "ghost"}
                size="sm"
                onClick={handleEditToggle}
                className={editing ? "border-red-300 text-red-600 hover:bg-red-50" : "text-ocean hover:bg-ocean/10"}
                data-testid="edit-profile-btn"
              >
                {editing ? <><X className="w-4 h-4 mr-1" /> Cancel</> : <><Edit3 className="w-4 h-4 mr-1" /> Edit</>}
              </Button>
            </div>

            <div className="space-y-3.5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-ocean/70 text-sm">First Name</Label>
                  <Input
                    value={editing ? profile.first_name : (user?.first_name || '')}
                    onChange={(e) => setProfile(p => ({ ...p, first_name: e.target.value }))}
                    disabled={!editing}
                    placeholder="First name"
                    className={`mt-1 h-10 ${!editing ? 'bg-ocean/5 border-ocean/10' : 'border-ocean/30 focus:border-ocean'}`}
                    data-testid="profile-first-name"
                  />
                </div>
                <div>
                  <Label className="text-ocean/70 text-sm">Last Name</Label>
                  <Input
                    value={editing ? profile.last_name : (user?.last_name || '')}
                    onChange={(e) => setProfile(p => ({ ...p, last_name: e.target.value }))}
                    disabled={!editing}
                    placeholder="Last name"
                    className={`mt-1 h-10 ${!editing ? 'bg-ocean/5 border-ocean/10' : 'border-ocean/30 focus:border-ocean'}`}
                    data-testid="profile-last-name"
                  />
                </div>
              </div>

              <div>
                <Label className="text-ocean/70 text-sm flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" /> Email Address
                </Label>
                <Input value={user?.email || ''} disabled className="mt-1 h-10 bg-ocean/5 border-ocean/10" data-testid="profile-email" />
                <p className="text-xs text-ocean/40 mt-1">Email cannot be changed</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-ocean/70 text-sm flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" /> Phone Number
                  </Label>
                  <Input
                    value={editing ? profile.phone : (user?.phone || '')}
                    onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))}
                    disabled={!editing}
                    placeholder="+61 400 000 000"
                    className={`mt-1 h-10 ${!editing ? 'bg-ocean/5 border-ocean/10' : 'border-ocean/30 focus:border-ocean'}`}
                    data-testid="profile-phone"
                  />
                </div>
                <div>
                  <Label className="text-ocean/70 text-sm flex items-center gap-1">
                    <Globe className="w-3.5 h-3.5" /> Nationality
                  </Label>
                  <Input
                    value={editing ? profile.nationality : (user?.nationality || '')}
                    onChange={(e) => setProfile(p => ({ ...p, nationality: e.target.value }))}
                    disabled={!editing}
                    placeholder="e.g., Australian"
                    className={`mt-1 h-10 ${!editing ? 'bg-ocean/5 border-ocean/10' : 'border-ocean/30 focus:border-ocean'}`}
                    data-testid="profile-nationality"
                  />
                </div>
              </div>

              <div>
                <Label className="text-ocean/70 text-sm">Account Created</Label>
                <Input value={user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'} disabled className="mt-1 h-10 bg-ocean/5 border-ocean/10" />
              </div>

              {editing && (
                <Button onClick={handleSaveProfile} disabled={saving}
                  className="w-full h-11 bg-gradient-to-r from-ocean to-sky text-white rounded-full font-semibold mt-2 shadow-lg" data-testid="save-profile-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
            </div>
          </motion.div>

          {/* Notification Preferences */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 p-7 mb-5">
            <h3 className="font-semibold text-ocean mb-4 flex items-center gap-2" data-testid="notification-settings-header">
              <Bell className="w-5 h-5 text-gold" /> Notification Preferences
            </h3>
            <div className="flex items-center justify-between p-3.5 bg-ocean/5 rounded-xl border border-ocean/10">
              <div>
                <p className="font-medium text-ocean">Push Notifications</p>
                <p className="text-sm text-ocean/60 mt-0.5">Get alerts for trip updates, companion invites, and reminders</p>
              </div>
              <NotificationToggle />
            </div>
          </motion.div>

          {/* Change Password */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 p-7">
            <h3 className="font-semibold text-ocean mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-gold" /> Change Password
            </h3>
            <div className="space-y-3.5">
              <div>
                <Label className="text-ocean/70 text-sm">Current Password</Label>
                <Input type="password" value={passwords.current} onChange={(e) => setPasswords(p => ({ ...p, current: e.target.value }))}
                  placeholder="Enter current password" className="mt-1 h-10 border-ocean/20" data-testid="current-password-input" />
              </div>
              <div>
                <Label className="text-ocean/70 text-sm">New Password</Label>
                <Input type="password" value={passwords.newPass} onChange={(e) => setPasswords(p => ({ ...p, newPass: e.target.value }))}
                  placeholder="Min. 6 characters" className="mt-1 h-10 border-ocean/20" data-testid="new-password-input" />
              </div>
              <div>
                <Label className="text-ocean/70 text-sm">Confirm New Password</Label>
                <Input type="password" value={passwords.confirm} onChange={(e) => setPasswords(p => ({ ...p, confirm: e.target.value }))}
                  placeholder="Confirm new password" className="mt-1 h-10 border-ocean/20" data-testid="confirm-password-input" />
              </div>
              <Button onClick={handleChangePassword} disabled={changingPassword}
                className="w-full h-11 bg-gradient-to-r from-ocean to-sky text-white rounded-full font-semibold shadow-lg" data-testid="change-password-btn">
                {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {changingPassword ? 'Updating...' : 'Update Password'}
              </Button>
            </div>
          </motion.div>

          <div className="flex justify-center mt-8">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white border border-white/30 hover:bg-white/10 rounded-full px-6 py-2 text-sm font-medium flex items-center gap-2 transition-all" data-testid="back-to-dashboard-bottom">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
